import React, { useEffect, useRef, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Crown,
  Star,
  Settings,
  Users,
  Edit as EditIcon,
  Trash2,
  X,
  Gift,
  Sparkles,
  Trophy,
} from "lucide-react";
import Header3 from "../components/Header3";
import { useAuth } from "../context/auth";
import { BASE_URL } from "../config/BaseUrls";
import axios from "../AxiosInstance";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Footer2 from "../components/Footer2";
import SocketContext from "../context/SocketContext";
import { LanguageContext } from "../context/LanguageContext";
import { resolveBookImageUrl } from "../utils/urlHelpers";
/* ---------- Primitives ---------- */
const Container = ({ children }) => (
  <div className="mx-auto w-full max-w-7xl px-[16px] sm:px-[24px] lg:px-[32px]">
    {children}
  </div>
);

const Pill = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full px-[10px] py-[2px] text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

const SectionTitle = ({ children, action }) => (
  <div className="mb-4 flex items-end justify-between">
    <h2 className="text-xl font-semibold tracking-tight">{children}</h2>
    {action}
  </div>
);

const Button = ({
  variant = "primary",
  children,
  className = "",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center rounded-2xl px-[20px] py-[8px] text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[#34c3c9] text-white hover:bg-[#2aa5ab] focus:ring-[#34c3c9]",
    secondary:
      "bg-white text-[#34c3c9] hover:bg-[#34c3c9]/10 ring-1 ring-[#34c3c9] focus:ring-[#34c3c9]",
    ghost: "text-[#34c3c9] hover:bg-[#34c3c9]/10",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Card = ({ children, className = "", onClick }) => (
  <div
    onClick={onClick}
    className={`rounded-3xl bg-white p-[20px] shadow-sm ring-1 ring-[#f2f2f2] ${className}`}
  >
    {children}
  </div>
);

const Progress = ({ value }) => (
  <div className="h-2 w-full overflow-hidden rounded-full bg-[#f2f2f2]">
    <motion.div
      className="h-2 rounded-full bg-[#34c3c9]"
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
    />
  </div>
);

// Books & Reviews shelves (covers only; Goodreads-like sizing)
const wantToRead = [
  {
    id: "w1",
    cover:
      "https://images.unsplash.com/photo-1541963463532-d68292c34b19?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "w2",
    cover:
      "https://images.unsplash.com/photo-1528207776546-365bb710ee93?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "w3",
    cover:
      "https://images.unsplash.com/photo-1544717305-996b815c338c?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "w4",
    cover:
      "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=300&auto=format&fit=crop",
  },
];
const read = [
  {
    id: "r1",
    cover:
      "https://images.unsplash.com/photo-1473755504818-b72b6dfdc226?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "r2",
    cover:
      "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "r3",
    cover:
      "https://images.unsplash.com/photo-1542089363-5c76c7285f37?q=80&w=300&auto=format&fit=crop",
  },
  {
    id: "r4",
    cover:
      "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?q=80&w=300&auto=format&fit=crop",
  },
];
const MobileActionSheet = ({ user, open, onClose, lg }) => {
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/45" onClick={onClose} />
          <motion.div
            initial={{ y: 64, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 64, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="absolute bottom-0 inset-x-0 z-10 rounded-t-3xl bg-white p-5 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Create options"
          >
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-200" />
            <div className="grid grid-cols-1 gap-2">
              <Button
                className="w-full"
                onClick={() => navigate("/createmeetup")}
              >
                {lg?.createBookDuo || "Create Book Duo"}
              </Button>

              {/* OLD CODE - Future use: Ambassador conditional rendering */}
              {/* {user?.isAmbassador && (
                <Button
                  className="w-full"
                  onClick={() => navigate("/silentreading")}
                >
                  {lg?.createSilentReading || "Create Silent Reading"}
                </Button>
              )}
              {user?.isAmbassador && (
                <Button
                  className="w-full"
                  onClick={() => navigate("/booksdetails")}
                >
                  {lg?.createBookClub || "Create Book Club"}
                </Button>
              )} */}
              {/* NEW CODE: All users can create events */}
              <Button
                className="w-full"
                onClick={() => navigate("/silentreading")}
              >
                {lg?.createSilentReading || "Create Silent Reading"}
              </Button>
              <Button
                className="w-full"
                onClick={() => navigate("/booksdetails")}
              >
                {lg?.createBookClub || "Create Book Club"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
/* ---------- Profile Card ---------- */
const ProfileCard = ({ user, lg }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // ✅ Extract event counts safely
  const eventCount = user?.eventCount || {
    bookClub: 0,
    bookDuo: 0,
    silentReading: 0,
  };

  // ✅ Calculate total points
  const totalPoints =
    Number(eventCount.bookClub) * 12 +
    Number(eventCount.bookDuo) * 8 +
    Number(eventCount.silentReading) * 5;

  // ✅ Determine level and progress
  let level = lg?.level1 || "Level 1";
  let nextLevelPoints = 1000;
  let currentLevelMin = 0;

  if (totalPoints >= 1000 && totalPoints < 2500) {
    level = lg?.level2 || "Level 2";
    currentLevelMin = 1000;
    nextLevelPoints = 2500;
  } else if (totalPoints >= 2500 && totalPoints < 5000) {
    level = lg?.level3 || "Level 3";
    currentLevelMin = 2500;
    nextLevelPoints = 5000;
  } else if (totalPoints >= 5000) {
    level = lg?.level4 || "Level 4";
    nextLevelPoints = null; // Max level
  }

  // ✅ Progress percentage toward next level
  const progressPercent =
    nextLevelPoints === null
      ? 100
      : Math.min(
        100,
        Math.max(
          0,
          Number(
            (
              ((totalPoints - currentLevelMin) /
                (nextLevelPoints - currentLevelMin)) *
              100
            ).toFixed(1)
          )
        )
      );

  console.log(totalPoints, progressPercent, "progressPercent");
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#f2f2f2] to-white" />

      <div className="flex flex-col items-start gap-[16px] sm:flex-row sm:items-center sm:justify-between">
        {/* Profile Info */}
        <div className="flex items-center gap-[16px]">
          <img
            src={
              user?.profileImage?user?.profileImage?.startsWith("http")? user?.profileImage:
                 BASE_URL + "/uploads/" + user.profileImage
                : require("../pages/image/addphotoicon.png")
            }
            alt="Profile"
            style={{ objectFit: "contain" }}
            className="h-[64px] w-[64px] rounded-full ring-4 ring-white"
          />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {user?.name}
            </h1>

            <div className="mt-[8px] flex flex-col gap-1">
              <Pill className="max-w-max bg-[#34c3c9]/20 text-[#34c3c9]">
                <Crown size={14} className="mr-[4px]" />
                {user?.isAmbassador ? (lg?.ambassador || "Ambassador") : (lg?.member || "Member")}
              </Pill>

              {user?.isAmbassador && (
                <Pill className="max-w-max bg-amber-100 text-amber-800">
                  <Star size={14} className="mr-[4px]" /> {level}
                </Pill>
              )}
            </div>

            {/* ✅ Progress Bar for Next Level */}
            {user?.isAmbassador && (
              <div className="mt-[12px] w-56">
                <Progress value={progressPercent} />
                {nextLevelPoints ? (
                  <p className="mt-1 text-xs text-[#a6a6a6]">
                    {progressPercent}% {lg?.toLevel || "to"}
                    <strong>
                      {level === (lg?.level1 || "Level 1")
                        ? ` ${lg?.level2 || "Level 2"}`
                        : level === (lg?.level2 || "Level 2")
                          ? ` ${lg?.level3 || "Level 3"}`
                          : ` ${lg?.level4 || "Level 4"}`}
                    </strong>
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-green-600 font-semibold">
                    {lg?.highestLevel || "🎉 You've reached the highest level!"}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="ml-0 flex flex-row flex-nowrap gap-2">
          <Button variant="secondary" onClick={() => navigate("/profile")}>
            {lg?.edit || "Edit"} <span className="hidden sm:block pl-1">{lg?.editProfile?.split(" ")[1] || "Profile"}</span>
          </Button>

          <div className="hidden sm:flex md:flex-row sm:gap-2">
            {user?.isAmbassador && (
              <>
                <Button onClick={() => navigate("/booksdetails")}>
                  {lg?.createBookClub || "Create Book Club"}
                </Button>
                <Button onClick={() => navigate("/silentreading")}>
                  {lg?.createSilentReading || "Create Silent Reading"}
                </Button>
              </>
            )}
            <Button onClick={() => navigate("/createmeetup")}>
              {lg?.createBookDuo || "Create Book Duo"}
            </Button>
          </div>

          <Button className="block sm:hidden" onClick={() => setOpen(true)}>
            {lg?.createEvent || "+ Create Event"}
          </Button>

          <MobileActionSheet
            user={user}
            open={open}
            onClose={() => setOpen(false)}
            lg={lg}
          />
        </div>
      </div>
    </Card>
  );
};

/* ---------- Helpers ---------- */
function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside?.();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

/* ---------- Popovers & Modals ---------- */
const ManageMenu = ({ onAttendees, onEdit, onCancel, onClose, lg }) => {
  const ref = useRef(null);
  useClickOutside(ref, onClose);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      className="absolute right-0 top-full z-30 mt-[8px] w-56 overflow-hidden rounded-xl border border-[#e5e5e5] bg-white py-[4px] shadow-xl"
    >
      {onAttendees && (
        <button
          onClick={onAttendees}
          className="flex w-full items-center gap-[8px] px-[16px] py-[8px] text-left text-sm hover:bg-[#f8f8f8]"
        >
          <Users size={16} /> {lg?.attendeesList || "Attendees list"}
        </button>
      )}
      <button
        onClick={onEdit}
        className="flex w-full items-center gap-[8px] px-[16px] py-[8px] text-left text-sm hover:bg-[#f8f8f8]"
      >
        <EditIcon size={16} /> {lg?.edit || "Edit"}
      </button>
      <button
        onClick={onCancel}
        className="flex w-full items-center gap-[8px] px-[16px] py-[8px] text-left text-sm text-rose-600 hover:bg-rose-50"
      >
        <Trash2 size={16} /> {lg?.cancel || "Cancel"}
      </button>
    </motion.div>
  );
};

const WriteMenu = ({
  book,
  selectedOptions,
  handleOptionChange,
  onClose,
  ind,
  lg,
}) => {
  const ref = useRef(null);
  useClickOutside(ref, onClose);
  console.log(book, "book handleOptionChange");
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      className="absolute  top-full left-[-60px] z-30 mt-[8px] w-[9rem] overflow-hidden rounded-xl border border-[#e5e5e5] bg-white py-[8px] shadow-xl"
    >
      <div className="flex flex-col gap-2 px-3 py-2 text-sm">
        {/* ✅ Want to Read */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`group-${book?._id}`}
            id={`want-${book?._id}`}
            checked={selectedOptions[book?._id] === "Want to Read"}
            onChange={() => handleOptionChange(ind, "Want to Read")}
          />
          {lg?.wantToRead || "Want to Read"}
        </label>

        {/* ✅ Read */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={`group-${book?._id}`}
            id={`read-${book?._id}`}
            checked={selectedOptions[book?._id] === "Read"}
            onChange={() => handleOptionChange(ind, "Read")}
          />
          {lg?.read || "Read"}
        </label>

        {/* ✅ Remove */}
        <label className="flex items-center gap-2 cursor-pointer text-rose-600">
          <input
            type="radio"
            name={`group-${book?._id}`}
            id={`remove-${book?._id}`}
            checked={selectedOptions[book?._id] === "Remove"}
            onChange={() => handleOptionChange(ind, "Remove")}
          />
          {lg?.remove || "Remove"}
        </label>
      </div>
    </motion.div>
  );
};

const AttendeesModal = ({
  open,
  onClose,
  attendees,
  handleGroupChatCreation,
  lg,
  onRemoveAttendee,
  isOrganizer = false,
  removingAttendee = null,
}) => {
  console.log(attendees, "attendees");
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="relative z-10 w-full max-w-md rounded-[28px] border-2 border-[#34c3c9] bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attendees-title"
          >
            <button
              className="absolute right-[16px] top-4 text-slate-500"
              onClick={onClose}
              aria-label="Close attendees"
            >
              <X />
            </button>
            <h3
              id="attendees-title"
              className="mb-[16px] text-lg font-semibold text-slate-900"
            >
              {lg?.attendees || "Attendees"}
            </h3>
            <div className="max-h-72 overflow-auto pr-1">
              <ul className="divide-y divide-[#f1f1f1]">
                {attendees.length === 0 ? (
                  <div className="text-center fs-5 fw-bold ftzgern my-3 text-muted">
                    {lg?.noAttendeesYet || "No attendees yet."}
                  </div>
                ) : null}
                {(attendees || []).map((p, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-3 py-2">
                    <div className="flex items-center gap-3 flex-1">
                      <img
                        src={
                          p?.profileImage
                            ?p?.profileImage?.startsWith("http")?p?.profileImage: `${BASE_URL}/uploads/${p.profileImage}`
                            : require("../pages/image/addphotoicon.png")
                        }
                        alt=""
                        style={{ objectFit: "contain" }}
                        className="h-9 w-9 rounded-full object-cover"
                      />
                      <div
                        onClick={() =>
                          navigate(`/ambassadorprofile?id=${p?._id}`)
                        }
                        className="cursor-pointer text-sm font-medium text-slate-900 hover:text-[#34c3c9] hover:underline"
                      >
                        {p.name}
                      </div>
                    </div>
                    {isOrganizer && onRemoveAttendee && (
                      <button
                        onClick={() => onRemoveAttendee(p._id, p.name || p.email || "this attendee")}
                        disabled={removingAttendee === p._id}
                        className="px-3 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {removingAttendee === p._id ? "Removing..." : "Remove"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-5">
              <Button
                className="w-full"
                onClick={(e) => handleGroupChatCreation(e)}
              >
                {lg?.chatWithGroup || "Chat with the group"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------- Event Cards (Leading/Joining tabs) ---------- */
const BookCard = ({
  item,
  mode = "manage",
  allowAttendees = false,
  onOpenAttendees,
  onCancel,
  isFull = false,
  setGroupChatName,
  onClick,
  lg,
}) => {
  console.log(isFull, "Is Full");
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setMenuOpen(false));
  const shortToFullMonth = {
    Jan: "January",
    Feb: "February",
    Mar: "March",
    Apr: "April",
    May: "May",
    Jun: "June",
    Jul: "July",
    Aug: "August",
    Sep: "September",
    Oct: "October",
    Nov: "November",
    Dec: "December",
  };

  // Example:
  const shortMonth = item?.dateInfo?.month; // e.g., "Jan"
  const fullMonth = shortToFullMonth[shortMonth] || shortMonth; // fallback in case undefined

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <Card
        className="relative cursor-pointer flex h-full flex-col"
        onClick={onClick}
      >
        <div className="flex items-start gap-4">
          <div
            className={`relative h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-gradient-to-br ${item?.cover?.bg} ring-1 ring-[#f2f2f2]`}
          >
            <div className="relative h-[100%]">
              <img
                className={`w-100 h-[100%] ${isFull ? "opacity-30" : "opacity-100"
                  }`}
                src={
    item?.name === "Silent Reading"
      ? require("./image/book7.jpg")
      : item?.image
      ? resolveBookImageUrl(item.image) // ✅ prepend base only when needed
      : "/default-placeholder.jpg" // ✅ fallback if no image
  }

                alt=""
              />
              {isFull &&
                (item?.name == "Book Club" ||
                  item?.name == "Silent Reading") && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%) rotate(301deg)",
                      color: "red",
                      fontSize: "14px",
                      fontWeight: "bold",
                      border: "2px solid red",
                      padding: "5px 15px",
                      textTransform: "uppercase",
                      backgroundColor: "rgba(255,255,255,0.6)",
                      pointerEvents: "none",
                    }}
                  >
                    {lg?.full || "FULL"}
                  </div>
                )}
            </div>
            {/* <div className="absolute inset-0 grid place-items-center text-sm font-semibold text-slate-700/70">
              {item?.cover?.label}
            </div> */}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-semibold leading-tight">
              {item?.name == "Silent Reading" ? (lg?.yourOwnBook || "Your own book") : item?.title}
            </h3>
            <div className="mt-2 flex flex-col gap-1 text-sm text-[#6b7280]">
              <span className="inline-flex items-center gap-1">
                <CalendarDays size={16} />{" "}
                {item?.name == "Book Duo"
                  ? (lg?.tbd || "TBD")
                  : (() => {
                      // Translate day abbreviation
                      const dayMap = {
                        "Mon": lg?.dayMon || "Mon",
                        "Tue": lg?.dayTue || "Tue",
                        "Wed": lg?.dayWed || "Wed",
                        "Thu": lg?.dayThu || "Thu",
                        "Fri": lg?.dayFri || "Fri",
                        "Sat": lg?.daySat || "Sat",
                        "Sun": lg?.daySun || "Sun",
                      };
                      const translatedDay = dayMap[item?.dateInfo?.dayName] || item?.dateInfo?.dayName;
                      return translatedDay + ",  " + item?.dateInfo?.day + " " + fullMonth + " " + item?.dateInfo?.year;
                    })()}
              </span>
              <span>{item?.type}</span>
              <span>{lg?.ageLabel || "Age:"} {item?.age}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end">
          {mode === "manage" ? (
            <div className="relative" ref={wrapRef}>
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
              >
                <Settings size={16} className="mr-2" /> {lg?.manage || "Manage"}
              </Button>
              <AnimatePresence>
                {menuOpen && (
                  <ManageMenu
                    onAttendees={
                      allowAttendees
                        ? (e) => {
                          e.stopPropagation();
                          setMenuOpen(false);
                          onOpenAttendees?.(item?._id);
                          setGroupChatName(item);
                        }
                        : undefined
                    }
                    onEdit={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      let url = "";
                      if (item?.name == "Book Club") {
                        url = `/booksdetails/${item?._id}`;
                      } else if (item?.name == "Silent Reading") {
                        url = `/silentreading/${item?._id}`;
                      } else if (item?.name == "Book Duo") {
                        url = `/createmeetup/${item?._id}`;
                      }
                      navigate(url);
                    }}
                    onCancel={(e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      return onCancel?.(item?._id);
                    }}
                    onClose={() => setMenuOpen(false)}
                    lg={lg}
                  />
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onCancel?.(item?._id);
              }}
            >
              {lg?.cancelRSVP || "Cancel RSVP"}
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

/* ---------- Books & Reviews ---------- */
const ShelfEditMenu = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  return (
    <div className="relative">
      <button
        type="button"
        className="text-sm font-medium text-[#34c3c9] underline underline-offset-4 hover:opacity-90"
        onClick={() => setOpen((v) => !v)}
      >
        Edit
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="absolute left-0 top-full z-20 mt-2 w-64 rounded-xl border border-[#e5e5e5] bg-white p-4 shadow-xl"
          >
            <div className="space-y-3 text-sm text-slate-800">
              {["want to read", "read", "remove"].map((opt) => (
                <label
                  key={opt}
                  className="flex cursor-pointer items-center gap-3"
                >
                  <input
                    type="radio"
                    name="shelf"
                    value={opt}
                    checked={value === opt}
                    onChange={() => {
                      onChange?.(opt);
                      setOpen(false);
                    }}
                  />
                  <span className="capitalize">{opt}</span>
                </label>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Stars = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => {
        const filled = i < full || (i === full && half);
        const halfFill = i === full && half;
        return (
          <div key={i} className="relative h-4 w-4">
            <Star className="absolute inset-0 h-4 w-4 text-gray-300" />
            {filled && (
              <Star
                className="absolute inset-0 h-4 w-4 text-yellow-400"
                style={halfFill ? { clipPath: "inset(0 50% 0 0)" } : {}}
                fill="#fbbf24"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Loyalty Programme ---------- */
const LevelCard = ({
  level,
  title,
  requirement,
  reward,
  progress = 0,
  locked = false,
}) => (
  <motion.div
    whileHover={{ y: -4 }}
    className={`rounded-3xl p-5 ring-1 ring-[#dde2e6] shadow-sm bg-white ${locked ? "opacity-80" : ""
      }`}
  >
    <div className="mb-2 flex items-center justify-between">
      <Pill className="bg-[#34c3c9]/15 text-[#0f766e]">Level {level}</Pill>
      {progress >= 100 ? (
        <Trophy className="h-5 w-5 text-amber-500" />
      ) : (
        <Sparkles className="h-5 w-5 text-[#34c3c9]" />
      )}
    </div>
    <h4 className="text-base font-semibold text-slate-900">{title}</h4>
    <p className="mt-2 text-sm text-slate-700">{requirement}</p>
    <div className="mt-3 text-sm">
      <span className="font-semibold">Award:</span> {reward || "—"}
    </div>
    <div className="mt-4">
      <Progress value={progress} />
      <div className="mt-1 text-right text-xs text-slate-500">
        {Math.min(progress, 100)}%
      </div>
    </div>
  </motion.div>
);

/* ---------- Tabs ---------- */
const Tabs = ({ value, onChange, user, lg }) => {
  const items = [
    { id: "leading", label: lg?.imLeading || "I'm Leading" },
    { id: "joining", label: lg?.imJoining || "I'm Joining" },
    { id: "reviews", label: lg?.booksAndReviews || "Books & Reviews" },
    { id: "loyalty", label: lg?.loyaltyProgram || "Loyalty Program" },
  ];

  return (
    <div className="mt-[24px] flex gap-2 overflow-x-auto rounded-2xl bg-[#f2f2f2] p-1">
      {items.map((it) => {
        // ✅ Only show "Loyalty Programme" if user is ambassador
        if (it.id === "loyalty" && !user?.isAmbassador) return null;

        return (
          <button
            key={it.id}
            onClick={() => onChange(it.id)}
            className={`whitespace-nowrap rounded-xl px-[16px] py-[8px] text-sm transition-colors ${value === it.id
                ? "bg-white font-bold text-[#34c3c9] shadow-sm ring-1 ring-[#34c3c9]/20"
                : "text-[#a6a6a6] hover:text-[#34c3c9]"
              }`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

/* ---------- Page ---------- */
const LeadingNew = () => {
  const { lg } = useContext(LanguageContext);
  const [auth, setAuth] = useAuth();
  const [groupChatName, setGroupChatName] = useState("");
  const socket = useContext(SocketContext);
  let userId = auth?.user?._id;
  const navigate = useNavigate();
  useEffect(() => {
    userId = auth?.user?._id;
  }, [auth?.user, navigate]);
  const [user, setUser] = useState(null);
  const token = auth?.token; // <-- Your actual JWT

  const [tab, setTab] = useState("leading"); // Open on loyalty to showcase
  const [attendeesOpenFor, setAttendeesOpenFor] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [cancelType, setCancelType] = useState(null); // "leading" or "joined"
  const [showJoined, setShowJoined] = useState(false);
  const [bookss, setBookss] = useState([]);
  const [bookssRead, setBookssRead] = useState([]);

  const openAttendees = (eventId) => {
    handleAttendeeFetch(eventId);

  };
  const closeAttendees = () => {
    setAttendeesOpenFor(null);
    setAttendees([]);
    setSelectedEventForAttendees(null);
  };
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const fetchWantToReadBooks = async () => {
    try {
      const res = await axios.get(`api/v1/review/book-statuswent/${userId}`);
      console.log("Fetched books:", res.data); // 👈 Check what's actually returned
      setBookss(res.data);
    } catch (error) {
      console.error("Failed to fetch", error);
    }
  };
  useEffect(() => {


    if (userId) fetchWantToReadBooks();
  }, [userId]);
  const fetchReadBooks = async () => {
    try {
      const res = await axios.get(`api/v1/review/book-statusread/${userId}`);
      console.log("Fetched books:", res.data); // 👈 Check what's actually returned
      setBookssRead(res.data);
    } catch (error) {
      console.error("Failed to fetch", error);
    }
  };
  useEffect(() => {

    if (userId) fetchReadBooks();
  }, [userId]);
  const [ratedBooks, setRatedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const fetchRatedBooks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `api/v1/review/recently-rated/${userId}`
      );
      setRatedBooks(response.data.books);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching rated books:", error);
      setLoading(false);
    }
  };
  useEffect(() => {


    if (userId) fetchRatedBooks();
  }, [userId]);
  const handleBookClick = (book) => {
    if (!book) return;
    console.log("Book clicked:", book);
    const selectedBookObj = {
      bookId: undefined, // 👈 Use book.id if available
      bookCoverImage: book.coverImage, // <== This is correct
      title: book.title,
      author: book.author,
      descriptions: book.aboutBook, // ✅ add this
      titless: book.title,
      publisher: book.publisher,
      book: book.bookId, // 👈 Use book.bookId._id if available
      EAN: book.EAN, // 👈 Add EAN if available
    };
    setSelectedBook(book);
    localStorage.setItem(
      "selectedBookforreviews",
      JSON.stringify(selectedBookObj)
    );
    localStorage.setItem(
      "selectedBookforreviewsnew",
      JSON.stringify(selectedBookObj)
    );

    // ✅ Prefer EAN if available, otherwise fallback to bookId
    const queryParam = book?.EAN
      ? `ean=${encodeURIComponent(book.EAN)}`
      : `bookid=${encodeURIComponent(book?.bookId?._id)}`;

    // ✅ Decide source flag
    const sourceParam = book?.review?.offilioBookId
      ? "offilio=true"
      : "searchbook=true";

    navigate(`/reviews?${queryParam}&${sourceParam}`);
  };
  const handleEventTypeClick = (eventType, eventId) => {
    const eventRoutes = {
      "Book Duo": "/meetorganizer",
      "Book Club": "/groupevent",
      "Silent Reading": "/silentreadingabout",
    };

    const route = eventRoutes[eventType];
    if (route) {
      navigate(`${route}?bookid=${eventId}`);
    } else {
      console.error("No route defined for this event type:", eventType);
    }
  };
  // Goodreads-like cover size
  const coverClass =
    "h-[225px] w-[150px] overflow-hidden rounded-md ring-1 ring-[#eaeaea] shadow-sm bg-gray-100";

  // Dummy shelf state
  const [wShelf] = useState(
    Object.fromEntries(wantToRead.map((b) => [b.id, "want to read"]))
  );
  const [rShelf] = useState(
    Object.fromEntries(read.map((b) => [b.id, "read"]))
  );

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return; // Don't make API call if userId is undefined
      try {
        const res = await axios.get(`api/v1/auth/user-profile/${userId}`);
        setUser(res.data.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };

    fetchUser();
  }, [userId]);
  const [latestBooks, setLatestBooks] = useState([]);
  const fetchLatestBooks = async () => {
    try {
      const response = await axios.get(
        `api/v1/books/getlastestbooks/${userId}`
      );
      setLatestBooks(response.data.data);
    } catch (error) {
      console.error("Error fetching latest books:", error);
    }
  };
  useEffect(() => {
    fetchLatestBooks();
  }, [userId]);
  const bookClubBooks = latestBooks.filter((book) => book.name === "Book Club");

  const booksilentBooks = latestBooks.filter(
    (book) => book.name === "Silent Reading"
  );
  const bookDuoBooks = latestBooks.filter((book) => book.name === "Book Duo");
  const [attendees, setAttendees] = useState([]);
  const [selectedEventForAttendees, setSelectedEventForAttendees] = useState(null);
  const [removingAttendee, setRemovingAttendee] = useState(null);
  
  const handleAttendeeFetch = async (bookId) => {
    try {
      const res = await axios.get(`/api/v1/books/getattendeesusers/${bookId}`);
      setAttendees(res.data.attendees);
      
      // Find the event data to check if user is organizer
      const event = latestBooks.find((b) => b._id === bookId);
      setSelectedEventForAttendees(event);
    } catch (err) {
      console.error("Failed to fetch attendees", err);
    }
    setAttendeesOpenFor(bookId);
  };
  
  const handleRemoveAttendee = async (attendeeId, attendeeName) => {
    if (!selectedEventForAttendees?._id) {
      toast.error("Event information not available");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove ${attendeeName} from this event?`)) {
      return;
    }
    
    setRemovingAttendee(attendeeId);
    try {
      await axios.delete(
        `/api/v1/books/remove-attendee/${selectedEventForAttendees._id}/${attendeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`${attendeeName} removed successfully`);
      
      // Update the attendees list
      setAttendees(attendees.filter((a) => a._id !== attendeeId));
      
      // Refresh the latest books to update attendee counts
      fetchLatestBooks();
    } catch (error) {
      console.error("Error removing attendee:", error);
      toast.error(error.response?.data?.message || "Failed to remove attendee");
    } finally {
      setRemovingAttendee(null);
    }
  };
  const handleCancelAttendance = async (bookId) => {
    if (!bookId) {
      console.error("Book ID is missing!");
      return;
    }

    try {
      const res = await axios.put(`/api/v1/books/cancel-attendees/${bookId}`);
      console.log(res.data.message);
      setAttendees([]); // frontend se bhi hatao
      toast.success("Your attendance has been cancelled successfully.");
      fetchLatestBooks();
    } catch (err) {
      console.error("Cancel error:", err);
    }
  };

  //joining
  const [joinedBooks, setJoinedBooks] = useState([]);
  const [selectedOptionsRead, setSelectedOptionsRead] = useState({});
  useEffect(() => {
    const fetchJoinedBooks = async () => {
      // Assuming login user data is stored

      try {
        const res = await axios.get(`api/v1/books/user-joined-books/${userId}`);
        setJoinedBooks(Array.isArray(res.data) ? res.data : []); // ✅ ensures it's always an array
      } catch (err) {
        console.error("Error fetching joined books", err);
      }
    };

    fetchJoinedBooks();
  }, [userId]);
  const bookClubBooksJ = joinedBooks.filter(
    (book) =>
      book.name?.trim().toLowerCase() === "book club" &&
      book.attendees?.some((attendee) => attendee._id === userId)
  );

  const booksilentBooksJ = joinedBooks.filter(
    (book) =>
      book.name?.trim().toLowerCase() === "silent reading" &&
      book.attendees?.some((attendee) => attendee._id === userId)
  );

  const bookDuoBooksJ = joinedBooks.filter((book) => book.name === "Book Duo");
  const handleNotGoing = async (bookId) => {
    console.log("Calling API to remove user from book:", bookId);

    try {
      const res = await await axios.post(
        `/api/v1/books/notgoing/${bookId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`, // Replace with actual token
          },
        }
      );
      if (socket) {
        socket.emit("joiner_cancelled", { eventId: bookId, userId: userId });
      }

      console.log("API Response:", res.data);

      // Remove from frontend list
      setJoinedBooks((prev) =>
        Array.isArray(prev) ? prev.filter((book) => book._id !== bookId) : []
      );
      toast.success("Successfully removed from attendees"); // to confirm visually
    } catch (error) {
      console.error("Error removing user from book:", error);
      alert("API call failed — check console for error");
    }
  };
  const [menuOpenReview, setMenuOpenReview] = useState(false);
  const wrapRefReview = useRef(null);
  useClickOutside(wrapRefReview, () => setMenuOpenReview(false));
  const deleteBook = async (statusId) => {
    try {
      const res = await axios.delete(`api/v1/review/want/${statusId}`);
      console.log(res.data.message);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };
  const updateStatus = async (statusId, newStatus) => {
    try {
      const res = await axios.put(`api/v1/review/${statusId}`, {
        status: newStatus,
      });
      console.log(res.data.message);
    } catch (err) {
      console.error("Update failed", err);
    }
  };
  const handleOptionChange = async (index, value) => {
    console.log("clicked");
    const book = bookss[index];
    const statusId = book?._id;
    console.log(book, "book", index);
    if (!statusId) {
      console.warn("Status ID (statusId) missing");
      return;
    }

    if (value === "Remove") {
      await deleteBook(statusId);
      // Optionally, remove from local state
      const updatedBooks = [...bookss];
      updatedBooks.splice(index, 1);
      setBookss(updatedBooks);
      toast.success("Book Remove Successfully");
    } else {
      await updateStatus(statusId, value);
      toast.success(`Book status changed to "${value}" successfully.`);

      // 👇 Update selected option immediately
      setSelectedOptions((prev) => {
        const updatedOptions = {
          ...prev,
          [book.bookId]: value,
        };
        localStorage.setItem("selectedOptions", JSON.stringify(updatedOptions));
        return updatedOptions;
      });

      // setTimeout(() => {
      //   window.location.reload();
      // }, 5000);
    }
    fetchReadBooks()
    fetchWantToReadBooks()
    setOpenMenuId(false)
  };
  const handleOptionChangeRead = async (index, value) => {
    const book = bookssRead[index];
    const statusId = book?._id;

    if (!statusId) {
      console.warn("Status ID (statusId) missing");
      return;
    }

    if (value === "Remove") {
      await deleteBook(statusId);
      // Optionally, remove from local state
      toast.success("Book Remove Successfully");

      const updatedBooks = [...bookssRead];
      updatedBooks.splice(index, 1);
      setBookssRead(updatedBooks);
    } else {
      await updateStatus(statusId, value);

      toast.success(`Book status changed to "${value}" successfully.`);

      setSelectedOptionsRead((prev) => {
        const updatedOptionsRead = {
          ...prev,
          [book.bookId]: value,
        };
        localStorage.setItem(
          "selectedOptionsRead",
          JSON.stringify(updatedOptionsRead)
        );
        return updatedOptionsRead;
      });
      // setTimeout(() => {
      //   window.location.reload();
      // }, 5000);
    }
    fetchReadBooks()
    fetchWantToReadBooks()
    setOpenMenuId(false)
  };
  const handleGroupChatCreation = async (e) => {
    e.preventDefault();
    console.log("groupChatName", groupChatName);

    if (attendees.length === 0) {
      console.error("attendees is empty!");
      return;
    }
    try {
      const res = await axios.post(`/api/v1/chat/group`, {
        name: `${groupChatName?.name || ""} ${groupChatName?.title || ""}`.trim(),
        users: JSON.stringify([...attendees?.map((u) => u._id), user._id]),
        user: user._id,
        event: groupChatName?._id,
      });
      console.log("Group created successfully:", res.data);
      toast.success("Group created successfully.");
      navigate("/chatChak");
    } catch (err) {
      console.error("Error adding book:", err);
      toast.error("Failed to add book.");
    }
  };
  return (
    <>
      <div className="leaddashheader">
        <Header3 />
      </div>
      <div className="flex justify-between flex-col  min-h-screen bg-gradient-to-b from-white to-[#f2f2f2]">
        <main className="pb-16 pt-8">
          <Container>
            <ProfileCard user={user} lg={lg} />
            <Tabs value={tab} onChange={setTab} user={user} lg={lg} />

            {/* I’m Leading */}
            {tab === "leading" && (
              <div className="mt-8 space-y-10">
                {/* Only Ambassadors can see Book Clubs */}
                {user?.isAmbassador && (
                  <section>
                    <SectionTitle>{lg?.upcomingBookClubs || "Upcoming Book Clubs"}</SectionTitle>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {bookClubBooks?.length == 0 && (
                        <div className="text-[13px] font-[400] mt-[10px]">
                          {lg?.noEventsToShow || "No events to be shown."}
                        </div>
                      )}
                      {bookClubBooks?.map((c) => {
                        let spotsLeft =
                          Number(c?.maxAttendee) -
                          Number(c?.attendees?.length || 0);
                        const book = c; // For clarity in the event han dler
                        return (
                          <BookCard
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log("clicked");
                              const eventData = {
                                type: book.name,
                                location: book.city || "", // Use dynamic location
                                link: book.link || "",
                                title: book.title,
                                image: book.image,
                                author: book.author,
                                aboutBook: book.aboutBook,
                                pages: book.pageNumber,
                                genre: book.genre,
                                age: book.age,
                                aboutBook: book.aboutBook,

                                city: book.city,
                                language: book.language,
                                gender: book.gender,
                                detail: book.description,
                                eventType: book.type,
                                createdAt: book?.dateInfo,
                                time: book?.timeTrack,
                                userId: book?.userId,
                                link: book.link || "",
                                meetingLink: book?.meetingLink,
                                eventId: book._id,
                                attendees: book?.attendees,
                                LangLat: book?.location,
                                // type:
                                //   book.type.toLowerCase() === "on-line"
                                //     ? "Book Duo"
                                //     : "Book Club", // ✅ Condition Added
                              };

                              localStorage.setItem(
                                "eventDataNew",
                                JSON.stringify(eventData)
                              );
                              handleEventTypeClick(book.name, book._id);
                            }}
                            isFull={spotsLeft <= 0}
                            key={c.id}
                            item={c}
                            mode="manage"
                            allowAttendees
                            onCancel={(jo) => {
                              setShowJoined(jo);
                              setEventId(jo);
                            }}
                            onOpenAttendees={openAttendees}
                            setGroupChatName={setGroupChatName}
                            lg={lg}
                          />
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Only Ambassadors can see Silent Readings */}
                {user?.isAmbassador && (
                  <section>
                    <SectionTitle>{lg?.upcomingSilentReadings || "Upcoming Silent Readings"}</SectionTitle>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {booksilentBooks?.length == 0 && (
                        <div className="text-[13px] font-[400] mt-[10px]">
                          {lg?.noEventsToShow || "No events to be shown."}
                        </div>
                      )}
                      {booksilentBooks?.map((c) => {
                        let spotsLeft =
                          Number(c?.maxAttendee) -
                          Number(c?.attendees?.length || 0);
                        const book = c; // For clarity in the event handler
                        return (
                          <BookCard
                            onClick={() => {
                              const eventData = {
                                type: book.name,
                                location: book.city || "", // Use dynamic location
                                link: book.link || "",
                                title: book.title,
                                image: book.image,
                                author: book.author,
                                aboutBook: book.aboutBook,
                                pages: book.pageNumber,
                                genre: book.genre,
                                age: book.age,
                                aboutBook: book.aboutBook,

                                city: book.city,
                                language: book.language,
                                gender: book.gender,
                                detail: book.description,
                                eventType: book.type,
                                createdAt: book?.dateInfo,
                                time: book?.timeTrack,
                                userId: book?.userId,
                                link: book.link || "",
                                meetingLink: book?.meetingLink,
                                eventId: book._id,
                                attendees: book?.attendees,
                                LangLat: book?.location,
                              };

                              localStorage.setItem(
                                "eventDataNew",
                                JSON.stringify(eventData)
                              );
                              handleEventTypeClick(book.name, book._id);
                            }}
                            isFull={spotsLeft <= 0}
                            key={c.id}
                            item={c}
                            mode="manage"
                            allowAttendees
                            onCancel={(jo) => {
                              setShowJoined(jo);
                              setEventId(jo);
                            }}
                            onOpenAttendees={openAttendees}
                            setGroupChatName={setGroupChatName}
                            lg={lg}
                          />
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Everyone can see Book Duos */}
                <section>
                  <SectionTitle>{lg?.upcomingBookDuos || "Upcoming Book Duos"}</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {bookDuoBooks?.length == 0 && (
                      <div className="text-[13px] font-[400] mt-[10px]">
                        {lg?.noEventsToShow || "No events to be shown."}
                      </div>
                    )}
                    {bookDuoBooks?.map((c) => {
                      let spotsLeft =
                        Number(c?.maxAttendee) -
                        Number(c?.attendees?.length || 0);
                      const book = c; // For clarity in the event handler
                      return (
                        <BookCard
                          onClick={() => {
                            const eventData = {
                              type: book.name,
                              location: book.city || "", // Use dynamic location
                              link: book.link || "",
                              title: book.title,
                              image: book.image,
                              author: book.author,
                              aboutBook: book.aboutBook,
                              pages: book.pageNumber,
                              genre: book.genre,
                              age: book.age,
                              aboutBook: book.aboutBook,

                              city: book.city,
                              language: book.language,
                              gender: book.gender,
                              detail: book.description,
                              eventType: book.type,
                              createdAt: book?.dateInfo,
                              time: book?.timeTrack,
                              userId: book?.userId,
                              link: book.link || "",
                              meetingLink: book?.meetingLink,
                              eventId: book._id,
                              attendees: book?.attendees,
                              LangLat: book?.location,
                            };

                            localStorage.setItem(
                              "eventDataNew",
                              JSON.stringify(eventData)
                            );
                            handleEventTypeClick(book.name, book._id);
                          }}
                          isFull={spotsLeft <= 0}
                          key={c.id}
                          item={c}
                          mode="manage"
                          allowAttendees={true}
                          onCancel={(jo) => {
                            setShowJoined(jo);
                            setEventId(jo);
                          }}
                          onOpenAttendees={openAttendees}
                          setGroupChatName={setGroupChatName}
                          lg={lg}
                        />
                      );
                    })}
                  </div>
                </section>
              </div>
            )}

            {/* I Am Joining */}
            {tab === "joining" && (
              <div className="mt-8 space-y-10">
                <section>
                  <SectionTitle>{lg?.upcomingBookClubs || "Upcoming Book Clubs"}</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {bookClubBooksJ?.length == 0 && (
                      <div className="text-[13px] font-[400] mt-[10px]">
                        {lg?.noEventsToShow || "No events to be shown."}
                      </div>
                    )}
                    {bookClubBooksJ?.map((c) => {
                      let spotsLeft =
                        Number(c?.maxAttendee) -
                        Number(c?.attendees?.length || 0);
                      const book = c; // For clarity in the event handler
                      return (
                        <BookCard
                          onClick={() => {
                            const eventData = {
                              type: book.name,
                              location: book.city || "", // Use dynamic location
                              link: book.link || "",
                              title: book.title,
                              image: book.image,
                              author: book.author,
                              aboutBook: book.aboutBook,
                              pages: book.pageNumber,
                              genre: book.genre,
                              age: book.age,
                              aboutBook: book.aboutBook,

                              city: book.city,
                              language: book.language,
                              gender: book.gender,
                              detail: book.description,
                              eventType: book.type,
                              createdAt: book?.dateInfo,
                              time: book?.timeTrack,
                              userId: book?.userId,
                              link: book.link || "",
                              meetingLink: book?.meetingLink,
                              eventId: book._id,
                              attendees: book?.attendees,
                              LangLat: book?.location,
                            };

                            localStorage.setItem(
                              "eventDataNew",
                              JSON.stringify(eventData)
                            );
                            handleEventTypeClick(book.name, book._id);
                          }}
                          isFull={spotsLeft <= 0}
                          key={c.id}
                          item={c}
                          mode="cancel"
                          onCancel={(jo) => {
                            setCancelType("joined");
                            setShowJoined(true);
                            setEventId(jo);
                          }}
                          lg={lg}
                        />
                      );
                    })}
                  </div>
                </section>
                <section>
                  <SectionTitle>{lg?.upcomingSilentReadings || "Upcoming Silent Readings"}</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {booksilentBooksJ?.length == 0 && (
                      <div className="text-[13px] font-[400] mt-[10px]">
                        {lg?.noEventsToShow || "No events to be shown."}
                      </div>
                    )}
                    {booksilentBooksJ.map((r) => {
                      let spotsLeft =
                        Number(r?.maxAttendee) -
                        Number(r?.attendees?.length || 0);
                      const book = r; // For clarity in the event handler
                      return (
                        <BookCard
                          onClick={() => {
                            const eventData = {
                              type: book.name,
                              location: book.city || "", // Use dynamic location
                              link: book.link || "",
                              title: book.title,
                              image: book.image,
                              author: book.author,
                              aboutBook: book.aboutBook,
                              pages: book.pageNumber,
                              genre: book.genre,
                              age: book.age,
                              aboutBook: book.aboutBook,

                              city: book.city,
                              language: book.language,
                              gender: book.gender,
                              detail: book.description,
                              eventType: book.type,
                              createdAt: book?.dateInfo,
                              time: book?.timeTrack,
                              userId: book?.userId,
                              link: book.link || "",
                              meetingLink: book?.meetingLink,
                              eventId: book._id,
                              attendees: book?.attendees,
                              LangLat: book?.location,
                            };

                            localStorage.setItem(
                              "eventDataNew",
                              JSON.stringify(eventData)
                            );
                            handleEventTypeClick(book.name, book._id);
                          }}
                          isFull={spotsLeft <= 0}
                          key={r.id}
                          item={r}
                          mode="cancel"
                          onCancel={(jo) => {
                            setCancelType("joined");
                            setShowJoined(true);
                            setEventId(jo);
                          }}
                        />
                      );
                    })}
                  </div>
                </section>
                <section>
                  <SectionTitle>{lg?.upcomingBookDuos || "Upcoming Book Duos"}</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {bookDuoBooksJ?.length == 0 && (
                      <div className="text-[13px] font-[400] mt-[10px]">
                        {lg?.noEventsToShow || "No events to be shown."}
                      </div>
                    )}
                    {bookDuoBooksJ.map((d) => {
                      let spotsLeft =
                        Number(d?.maxAttendee) -
                        Number(d?.attendees?.length || 0);
                      const book = d; // For clarity in the event handler
                      return (
                        <BookCard
                          onClick={() => {
                            const eventData = {
                              type: book.name,
                              location: book.city || "", // Use dynamic location
                              link: book.link || "",
                              title: book.title,
                              image: book.image,
                              author: book.author,
                              aboutBook: book.aboutBook,
                              pages: book.pageNumber,
                              genre: book.genre,
                              age: book.age,
                              aboutBook: book.aboutBook,

                              city: book.city,
                              language: book.language,
                              gender: book.gender,
                              detail: book.description,
                              eventType: book.type,
                              createdAt: book?.dateInfo,
                              time: book?.timeTrack,
                              userId: book?.userId,
                              link: book.link || "",
                              meetingLink: book?.meetingLink,
                              eventId: book._id,
                              attendees: book?.attendees,
                              LangLat: book?.location,
                            };

                            localStorage.setItem(
                              "eventDataNew",
                              JSON.stringify(eventData)
                            );
                            handleEventTypeClick(book.name, book._id);
                          }}
                          isFull={spotsLeft <= 0}
                          key={d.id}
                          item={d}
                          mode="cancel"
                          onCancel={(jo) => {
                            setCancelType("joined");
                            setShowJoined(true);
                            setEventId(jo);
                          }}
                          setGroupChatName={setGroupChatName}
                        />
                      );
                    })}
                  </div>
                </section>
              </div>
            )}

            {/* Books & Reviews */}
            {tab === "reviews" && (
              <div className="mt-8 space-y-12">
                <section>
                  <SectionTitle>{lg?.wantToReadLower || "Want to read"}</SectionTitle>
                  <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {bookss?.map((b, ind) => {
                      console.log(openMenuId, b, "selectedOptions");
                      return (
                        <div key={b._id} className="flex flex-col items-center">
                          <motion.div
                            whileHover={{ y: -4 }}
                            className={coverClass}
                          >
                            <img
                              src={
                                resolveBookImageUrl(b?.coverImage)
                              }


                              alt=""
                              className="h-full w-full object-cover"
                            />
                          </motion.div>
                          <div className="relative">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setOpenMenuId(b?._id);
                              }}
                              className="cursor-pointer mt-2 text-sm font-medium text-[#34c3c9]"
                            >
                              {lg?.edit || "Edit"}
                            </div>
                            <AnimatePresence>
                              {openMenuId == b?._id && (
                                <WriteMenu
                                  book={b}
                                  selectedOptions={selectedOptions}
                                  handleOptionChange={handleOptionChange}
                                  onClose={() => setOpenMenuId(null)}
                                  ind={ind}
                                  lg={lg}
                                />
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
                <section>
                  <SectionTitle>{lg?.readLower || "Read"}</SectionTitle>
                  <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {bookssRead?.map((b, ind) => (
                      <div key={b.id} className="flex flex-col items-center">
                        <motion.div
                          whileHover={{ y: -4 }}
                          className={coverClass}
                        >
                          <img
                           src={
                                resolveBookImageUrl(b?.coverImage)
                              }
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </motion.div>
                        <div className="relative">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setOpenMenuId(b?._id);
                            }}
                            className="cursor-pointer mt-2 text-sm font-medium text-[#34c3c9]"
                          >
                            Edit
                          </div>
                          {openMenuId == b?._id && (
                            <WriteMenu
                              book={b}
                              selectedOptions={selectedOptionsRead}
                              handleOptionChange={handleOptionChangeRead}
                              onClose={() => setOpenMenuId(null)}
                              ind={ind}
                              lg={lg}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
                <section>
                  <SectionTitle>{lg?.reviewsLower || "Reviews"}</SectionTitle>
                  {ratedBooks.length === 0 && !loading && (
                    <p className="text-left text-muted w-100 ">
                      {lg?.noRatedBooks || "You Haven't rated any books yet."}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                    {ratedBooks.map((b) => (
                      <div
                        key={b.id}
                        className="flex cursor-pointer flex-col items-center"
                      >
                        <motion.div
                          whileHover={{ y: -4 }}
                          className={coverClass}
                          onClick={() => handleBookClick(b)}
                        >
                          <img
                            src={
                                  resolveBookImageUrl(b?.coverImage)
                              }
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </motion.div>
                        <div className="mt-2 flex items-center gap-1">
                          <Stars rating={b.rating} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {/* Loyalty Programme */}
            {tab === "loyalty" && (
              <div className="mt-8 space-y-8">
                {/* Intro banner */}
                <Card className="relative overflow-hidden">
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#e6fbfc] ring-1 ring-[#c6f2f4]">
                        <Gift className="h-7 w-7 text-[#34c3c9]" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold tracking-tight text-slate-900">
                          {lg?.giftsDiscounts || "Gifts and discounts on reading"}
                        </h2>
                        <p className="mt-1 text-slate-600">
                          {lg?.loyaltyDescription || "The more you read, the more you win! Enjoy discounts and perks as you level up."}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </Card>

                {/* Levels grid */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                  <LevelCard
                    level={1}
                    title={lg?.starterReader || "Starter Reader"}
                    requirement={lg?.starterReaderDesc || "Begin your journey as an Ofellio Ambassador and start leading your first reading experiences."}
                    reward={lg?.starterReaderReward || "Early recognition and access to future Ambassador perks."}
                    progress={22}
                  />
                  <LevelCard
                    level={2}
                    title={lg?.communityBuilder || "Community Builder"}
                    requirement={lg?.communityBuilderDesc || "Keep connecting readers and creating engaging book events within your community."}
                    reward={lg?.communityBuilderReward || "Priority access to new features and exclusive updates."}
                    progress={48}
                  />
                  <LevelCard
                    level={3}
                    title={lg?.proHost || "Pro Host"}
                    requirement={lg?.proHostDesc || "You're shaping meaningful reading moments and inspiring others through your events."}
                    reward={lg?.proHostReward || "Invitations to upcoming Ofellio initiatives and special surprises."}
                    progress={72}
                  />
                  <LevelCard
                    level={4}
                    title={lg?.legend || "Legend"}
                    requirement={lg?.legendDesc || "Continue inspiring readers worldwide — your leadership shapes the Ofellio community."}
                    reward={lg?.legendReward || "Unlock exclusive recognition and evolving benefits."}
                    progress={12}
                    locked
                  />
                </div>
              </div>
            )}
          </Container>
        </main>

        <div className="mt-5">
          <Footer2 />
        </div>

        {/* Portals */}
        <AttendeesModal
          open={!!attendeesOpenFor}
          onClose={closeAttendees}
          attendees={attendees}
          handleGroupChatCreation={handleGroupChatCreation}
          lg={lg}
          onRemoveAttendee={handleRemoveAttendee}
          isOrganizer={
            selectedEventForAttendees?.userId?._id === userId ||
            selectedEventForAttendees?.userId === userId
          }
          removingAttendee={removingAttendee}
        />
        {showJoined && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowJoined(false)}
            />
            <div className="relative z-10 w-full max-w-2xl rounded-2xl border-2 border-[#34c3c9] bg-white p-6 shadow-xl">
              <div className="mb-3 flex items-start justify-between">
                <h3 className="text-xl font-semibold text-[#0f172a]">
                  {lg?.cancelEvent || "Cancel Event"}
                </h3>
                <button
                  className="text-slate-500"
                  onClick={() => {
                    setShowJoined(false);
                  }}
                  aria-label="Close joined"
                >
                  ✕
                </button>
              </div>
              <p className="text-center text-sm sm:text-base text-slate-800 font-medium mb-2">
                <span className="font-semibold">
                  {cancelType === "joined"
                    ? (lg?.cancelRSVPConfirm || "Are you sure you want to cancel your RSVP?")
                    : (lg?.cancelEventConfirm || "Are you sure you want to cancel this event?")}
                </span>
                {cancelType !== "joined" && "!"}
              </p>
              <p className="text-center text-sm text-slate-600">
                {cancelType === "joined"
                  ? (lg?.cancelRSVPEmail || "A cancellation e-mail will be sent to Ambassador.")
                  : (lg?.cancelEventEmail || "A cancellation e-mail will be sent to the current attendees.")}
              </p>
              <div className="flex justify-center">
                <Button
                  className="mt-6 w-[150px] "
                  onClick={() => {
                    if (cancelType === "joined") {
                      setCancelType(null);
                      console.log(eventId, "eventId");
                      handleNotGoing(eventId);
                    } else {
                      handleCancelAttendance(eventId);
                    }
                    setShowJoined(false);
                  }}
                >
                  {lg?.cancel || "Cancel"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
    </>
  );
};

// --- Dev smoke tests (non-breaking in browsers) ---
try {
  console.assert(typeof Container === "function", "Container must be defined");
  console.assert(typeof Card === "function", "Card must be defined");
  console.assert(typeof LevelCard === "function", "LevelCard must be defined");
} catch (_) { }

export default LeadingNew;
